import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Copy, Grid2x2Plus, Link2, Pencil, Plus, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import sheetLogo from '../../assets/images/sheetlogo.png';
import { useAuth } from '../../contexts/AuthContext';
import { sheetService } from '../../services/sheetService';
import { sheetWorkspaceService } from '../../services/sheetWorkspaceService';
import { getWorkspaceCreatePath, getWorkspaceEditPath, getWorkspacePath } from '../../utils/sheetDomains';
import SheetsHeader from './SheetsHeader';

const getCompactDescription = (value = '', max = 110) => {
  const normalized = `${value || ''}`.trim();
  if (!normalized) return '';
  return normalized.length > max ? `${normalized.slice(0, max - 1).trim()}…` : normalized;
};

const WorkspacesList = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  const [activeView, setActiveView] = useState('workspaces');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cloningWorkspaceId, setCloningWorkspaceId] = useState('');

  useEffect(() => {
    if (!user?.uid) {
      setWorkspaces([]);
      setLoading(false);
      setError('');
      return;
    }

    let isActive = true;

    const loadWorkspaces = async () => {
      setLoading(true);
      setError('');
      try {
        const accessibleWorkspaces = activeView === 'templates'
          ? await sheetWorkspaceService.getTemplateWorkspaces()
          : await sheetWorkspaceService.getAccessibleWorkspaces(user.uid);
        if (!isActive) return;
        setWorkspaces(accessibleWorkspaces);
      } catch (loadError) {
        if (!isActive) return;
        setError(loadError?.message || 'Failed to load workspaces.');
      } finally {
        if (isActive) setLoading(false);
      }
    };

    loadWorkspaces();

    return () => {
      isActive = false;
    };
  }, [activeView, user?.uid]);

  const stats = useMemo(() => ({
    total: workspaces.length,
    shared: workspaces.filter((workspace) => workspace.userId !== user?.uid).length,
    templates: workspaces.filter((workspace) => workspace.isTemplate === true).length
  }), [user?.uid, workspaces]);

  const handleCloneWorkspace = async (workspace) => {
    const suggestedName = `${workspace?.title || 'Workspace'} Copy`.trim();
    const requestedName = window.prompt('Name the cloned workspace:', suggestedName);
    if (requestedName === null) return;

    const cleanName = requestedName.trim();
    if (!cleanName) {
      toast.error('A workspace name is required.');
      return;
    }

    try {
      setCloningWorkspaceId(workspace.id);
      const cloned = await sheetService.cloneWorkspace(workspace.id, user?.uid, {
        title: cleanName,
        includeRows: true
      });
      if (cloned?.workspace) {
        setWorkspaces((prev) => [cloned.workspace, ...prev.filter((item) => item.id !== cloned.workspace.id)]);
        navigate(getWorkspacePath(cloned.workspace.slug));
      }
      toast.success('Workspace cloned.');
    } catch (cloneError) {
      toast.error(cloneError?.message || 'Failed to clone workspace.');
    } finally {
      setCloningWorkspaceId('');
    }
  };

  return (
    <div className="space-y-3 w-full max-w-5xl mx-auto">
      <SheetsHeader
        activeTab="workspaces"
        badge="Workspaces"
        backPath="/sheets"
        backAriaLabel="Back to sheets"
        title="Workspace Directory"
        description="Create, organize, and open workspaces without mixing that management UI into the sheet library."
        actions={(
          <button
            onClick={() => navigate(getWorkspaceCreatePath())}
            className="inline-flex items-center gap-2 rounded-xl bg-[#008080] px-6 py-3 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-[#006d6d]"
          >
            <Plus className="h-4 w-4" />
            New Workspace
          </button>
        )}
      />

      <section className="grid gap-4 sm:grid-cols-2">
        <div className="rounded-[10px] border border-slate-200 bg-white p-5 shadow-md">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">Total Workspaces</p>
          <p className="mt-3 text-3xl font-bold text-slate-900">{stats.total}</p>
        </div>
        <div className="rounded-[10px] border border-slate-200 bg-white p-5 shadow-md">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-400">
            {activeView === 'templates' ? 'Published Templates' : 'Shared With You'}
          </p>
          <p className="mt-3 text-3xl font-bold text-slate-900">{activeView === 'templates' ? stats.templates : stats.shared}</p>
        </div>
      </section>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveView('workspaces')}
          className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition ${
            activeView === 'workspaces'
              ? 'border-[#008080]/30 bg-[#008080]/10 text-[#008080]'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          Workspaces
        </button>
        <button
          type="button"
          onClick={() => setActiveView('templates')}
          className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold transition ${
            activeView === 'templates'
              ? 'border-[#008080]/30 bg-[#008080]/10 text-[#008080]'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
          }`}
        >
          Templates
        </button>
      </div>

      {loading && (
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-10 text-center text-slate-500 shadow-sm">
          Loading workspaces...
        </div>
      )}

      {!loading && error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-6 py-5 text-rose-700 shadow-sm">
          {error}
        </div>
      )}

      {!user?.uid && !loading && (
        <div className="rounded-[10px] border border-slate-200 bg-white px-8 py-14 text-center shadow-md">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#008080]/10 text-[#008080]">
            <Grid2x2Plus className="h-7 w-7" />
          </div>
          <h2 className="mt-5 text-2xl font-semibold text-slate-900">Sign in to browse workspaces</h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500">
            Workspace templates and shared workspaces are available to authenticated users.
          </p>
        </div>
      )}

      {user?.uid && !loading && !error && workspaces.length === 0 && (
        <div className="rounded-[10px] border border-slate-200 bg-white px-8 py-14 text-center shadow-md">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-[#008080]/10 text-[#008080]">
            <Grid2x2Plus className="h-7 w-7" />
          </div>
          <h2 className="mt-5 text-2xl font-semibold text-slate-900">
            {activeView === 'templates' ? 'No workspace templates yet' : 'No workspaces yet'}
          </h2>
          <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500">
            {activeView === 'templates'
              ? 'Published workspace templates will appear here for quick cloning.'
              : 'Create a workspace to group related sheets, routing, references, and branding.'}
          </p>
          {activeView !== 'templates' && (
            <button
              onClick={() => navigate(getWorkspaceCreatePath())}
              className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#008080] px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#006d6d]"
            >
              <Plus className="h-4 w-4" />
              New Workspace
            </button>
          )}
        </div>
      )}

      {!loading && !error && workspaces.length > 0 && (
        <main className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {workspaces.map((workspace) => (
            <article
              key={workspace.id}
              className="flex h-full flex-col justify-between rounded-[10px] border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-lg font-bold text-slate-900">{workspace.title}</h2>
                      {workspace.isTemplate === true && (
                        <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-amber-700">
                          Template
                        </span>
                      )}
                      {workspace.userId !== user?.uid && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">
                          <Users className="h-3.5 w-3.5" />
                          Shared
                        </span>
                      )}
                    </div>
                    <p className="mt-2 min-h-[40px] text-sm leading-5 text-slate-500">
                      {getCompactDescription(
                        workspace.description || 'Grouped sheets, shared references, and a dedicated workspace surface.',
                        108
                      )}
                    </p>
                  </div>
                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-xl border border-slate-200 bg-slate-50">
                    <img
                      src={`${workspace.logoUrl || ''}`.trim() || sheetLogo}
                      alt={`${workspace.title} logo`}
                      className="h-full w-full object-contain p-2"
                    />
                  </div>
                </div>

                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm text-slate-600">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-700">
                      <Link2 className="h-4 w-4" />
                      {workspace.customDomain || workspace.slug || 'workspace'}
                    </span>
                    <span className="rounded-full bg-white px-3 py-1.5 text-xs font-semibold text-slate-600">
                      {workspace.visibility || 'private'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  onClick={() => handleCloneWorkspace(workspace)}
                  disabled={cloningWorkspaceId === workspace.id}
                  className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Copy className="h-4 w-4" />
                  {cloningWorkspaceId === workspace.id ? 'Cloning...' : 'Clone Workspace'}
                </button>
                {workspace.userId === user?.uid && (
                  <button
                    onClick={() => navigate(getWorkspaceEditPath(workspace.slug), {
                      state: { returnTo: getWorkspacePath(workspace.slug) }
                    })}
                    className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 transition hover:bg-slate-50"
                  >
                    <Pencil className="h-4 w-4" />
                    Edit Workspace
                  </button>
                )}
                <button
                  onClick={() => navigate(getWorkspacePath(workspace.slug))}
                  className="inline-flex items-center gap-2 rounded-lg bg-[#008080]/10 px-3 py-2 text-xs font-semibold text-[#008080] transition-colors hover:bg-[#008080]/15"
                >
                  Open Workspace
                  <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </article>
          ))}
        </main>
      )}
    </div>
  );
};

export default WorkspacesList;
